// src/features/hector-agent-casediscovery-v3/services/PoolEventEmitter.ts

import type { PoolEvents } from '../types/session.types';

/**
 * Signale l'exception levée par un abonné. Injecté par le propriétaire de
 * l'émetteur, qui seul sait où remonter un incident (ErrorService → PostHog).
 *
 * WHY injecté plutôt qu'importé : cet émetteur est sans dépendance à dessein
 * (cf. la classe ci-dessous). Y câbler `ErrorService` en dur y ferait entrer le
 * transport d'erreurs de l'app, et donc PostHog, dans un module que ses tests
 * instancient nu.
 */
export type PoolListenerErrorReporter = (error: unknown, event: string) => void;

const reportToConsole: PoolListenerErrorReporter = (error, event) => {
  console.error(`[PoolEventEmitter] listener de "${event}" a levé`, error);
};

// ─── PoolEventEmitter ────────────────────────────────────────────────────────

/**
 * Typed event emitter for the WebSocket connection pool.
 *
 * Zero-dependency, pure TypeScript. Designed to be owned by the singleton
 * `WebSocketPool` instance and consumed by React hooks / UI components.
 *
 * @example
 * ```ts
 * const emitter = new PoolEventEmitter();
 *
 * const unsub = emitter.on('status_changed', (key, status) => {
 *   console.log(key, status);
 * });
 *
 * emitter.emit('status_changed', 'session-abc', 'connected');
 *
 * unsub(); // remove listener
 * ```
 */
export class PoolEventEmitter {
  /**
   * Internal storage: event name → set of raw listener functions.
   *
   * We store listeners as `(...args: unknown[]) => void` to keep the Map
   * homogeneous; the public API enforces the correct tuple type per event via
   * the generic constraints on `on` / `off` / `emit`.
   */
  private listeners = new Map<string, Set<(...args: unknown[]) => void>>();

  private readonly reportListenerError: PoolListenerErrorReporter;

  constructor(reportListenerError: PoolListenerErrorReporter = reportToConsole) {
    this.reportListenerError = reportListenerError;
  }

  /**
   * Subscribe to a pool event.
   *
   * @param event - The event name (keyof {@link PoolEvents}).
   * @param listener - Callback invoked with the typed tuple arguments.
   * @returns An unsubscribe function — call it to remove the listener.
   */
  on<K extends keyof PoolEvents>(
    event: K,
    listener: (...args: PoolEvents[K]) => void,
  ): () => void {
    let bucket = this.listeners.get(event as string);
    if (!bucket) {
      bucket = new Set();
      this.listeners.set(event as string, bucket);
    }
    const raw = listener as (...args: unknown[]) => void;
    bucket.add(raw);
    return () => this.off(event, listener);
  }

  /**
   * Unsubscribe a previously registered listener.
   *
   * @param event - The event name.
   * @param listener - The exact listener reference passed to {@link on}.
   */
  off<K extends keyof PoolEvents>(
    event: K,
    listener: (...args: PoolEvents[K]) => void,
  ): void {
    this.listeners.get(event as string)?.delete(listener as (...args: unknown[]) => void);
  }

  /**
   * Emit an event, invoking all registered listeners synchronously.
   *
   * @param event - The event name.
   * @param args - The typed tuple arguments for this event.
   */
  emit<K extends keyof PoolEvents>(event: K, ...args: PoolEvents[K]): void {
    const bucket = this.listeners.get(event as string);
    if (!bucket) return;
    // Iterate over a snapshot to be safe if a listener modifies the set.
    for (const listener of [...bucket]) {
      // WHY chaque abonné isolé (incident 2026-09-11) : `emit` est appelé au milieu de
      // la machinerie du pool — notamment depuis le callback de perte de connexion, qui
      // doit encore relancer la reconnexion après. Sans cette garde, un seul abonné qui
      // lève (un store, un pont React) emportait tout l'appelant, et le tour en cours
      // ne se reprenait jamais. Un abonné défaillant ne doit dégrader que lui-même, et
      // ne doit pas non plus empêcher les abonnés suivants d'être notifiés.
      try {
        listener(...(args as unknown[]));
      } catch (err) {
        // Avaler SANS signaler transformerait une panne bruyante en panne
        // muette : c'est précisément la classe de défaut que l'incident du
        // 2026-09-11 a mis une journée à localiser, faute du moindre signal.
        try {
          this.reportListenerError(err, String(event));
        } catch {
          // Le report lui-même ne doit jamais casser la boucle de notification.
        }
      }
    }
  }

  /**
   * Remove all registered listeners for all events.
   *
   * Typically called when the pool is destroyed.
   */
  clear(): void {
    this.listeners.clear();
  }
}
